import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for managing cooldown functionality
 *
 * @param seconds - The cooldown duration in seconds
 * @returns Object with `cooldownSeconds`, `isInCooldown`, and `startCooldown()`
 *
 * @example
 * ```typescript
 * const { isInCooldown, startCooldown, cooldownSeconds } = useCooldown(5)
 * ```
 */
export const useCooldown = (seconds: number) => {
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)

  useEffect(() => {
    if (cooldownSeconds > 0) {
      intervalRef.current = setInterval(() => {
        setCooldownSeconds(prev => prev - 1)
      }, 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [cooldownSeconds])

  const startCooldown = useCallback(() => {
    setCooldownSeconds(seconds)
  }, [seconds])

  return {
    cooldownSeconds,
    isInCooldown: cooldownSeconds > 0,
    startCooldown,
  }
}
